import { useEffect, useId, useMemo, useState } from 'react'
import { SearchIcon, Download } from 'lucide-react'
import type { Column, ColumnDef, ColumnFiltersState, RowData, SortingState } from '@tanstack/react-table'
import {
  flexRender,
  getCoreRowModel,
  getFacetedMinMaxValues,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable
} from '@tanstack/react-table'
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar'
import { Badge } from '../components/ui/badge'
import { Checkbox } from '../components/ui/checkbox'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table'
import { Button } from '../components/ui/button'
import { cn, formatCurrency } from '../lib/utils'
import { Machine } from '../types/map'
import { getMachines } from '../lib/api'
import { useToast } from '../components/ui/use-toast'

declare module '@tanstack/react-table' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    filterVariant?: 'text' | 'range' | 'select'
  }
}

const columns: ColumnDef<Machine>[] = [
  {
    id: 'select',
    header: ({ table }) => (
      <Checkbox
        checked={table.getIsAllPageRowsSelected() || (table.getIsSomePageRowsSelected() && 'indeterminate')}
        onCheckedChange={value => table.toggleAllPageRowsSelected(!!value)}
        aria-label='Select all'
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onCheckedChange={value => row.toggleSelected(!!value)}
        aria-label='Select row'
      />
    )
  },
  {
    header: 'Machine Name',
    accessorKey: 'name',
    cell: ({ row }) => {
      const machine = row.original
      const imageUrl = machine.MachineMedia?.find(m => m.kind === 'area')?.url || machine.MachineMedia?.[0]?.url
      const fallback = machine.name.substring(0, 2).toUpperCase()
      
      return (
        <div className='flex items-center gap-3'>
          <Avatar className='rounded-sm h-10 w-10'>
            <AvatarImage src={imageUrl} alt={machine.name} />
            <AvatarFallback className='text-xs'>{fallback}</AvatarFallback>
          </Avatar>
          <div className='font-medium'>{machine.name}</div>
        </div>
      )
    }
  },
  {
    header: 'Venue Type',
    accessorKey: 'venueType',
    cell: ({ row }) => <div>{row.getValue('venueType') || '—'}</div>
  },
  {
    header: 'Venue Name',
    accessorKey: 'venueName',
    cell: ({ row }) => <div>{row.getValue('venueName') || '—'}</div>
  },
  {
    header: 'Status',
    accessorKey: 'status',
    cell: ({ row }) => {
      const status = row.getValue('status') as string || 'Live/Available'
      
      const styles = {
        'Live/Available': 'bg-emerald-100 text-emerald-700 border border-emerald-200',
        'Reserved': 'bg-amber-100 text-amber-700 border border-amber-200',
        'Coming Soon': 'bg-blue-100 text-blue-700 border border-blue-200'
      }[status] || 'bg-slate-100 text-slate-700 border border-slate-200'

      return (
        <Badge className={cn('border-none focus-visible:outline-none', styles)}>
          {status === 'Live/Available' ? 'Live' : status}
        </Badge>
      )
    },
    enableSorting: false,
    meta: {
      filterVariant: 'select'
    }
  },
  {
    header: 'Location',
    accessorKey: 'location',
    cell: ({ row }) => <div>{row.getValue('location') || '—'}</div>
  },
  {
    header: 'Availability Type',
    accessorKey: 'availabilityType',
    cell: ({ row }) => <div>{row.getValue('availabilityType') || '—'}</div>
  },
  {
    header: 'Available SKUs',
    accessorKey: 'availableSkus',
    cell: ({ row }) => {
      const skus = row.getValue('availableSkus') as number | null | undefined
      return <div>{skus !== null && skus !== undefined ? skus : '—'}</div>
    },
    meta: {
      filterVariant: 'range'
    }
  },
  {
    header: 'Cost',
    accessorKey: 'costCents',
    cell: ({ row }) => {
      const costCents = row.getValue('costCents') as number
      return <div>{formatCurrency(costCents)}</div>
    },
    enableSorting: false,
    meta: {
      filterVariant: 'range'
    }
  },
  {
    header: 'Machine Type',
    accessorKey: 'machineType',
    cell: ({ row }) => <div>{row.getValue('machineType') || '—'}</div>
  },
  {
    header: 'Resolution',
    accessorKey: 'resolution',
    cell: ({ row }) => <div>{row.getValue('resolution') || '—'}</div>
  },
  {
    header: 'Format',
    accessorKey: 'format',
    cell: ({ row }) => <div>{row.getValue('format') || '—'}</div>
  },
  {
    header: 'Screen Size',
    accessorKey: 'screenSize',
    cell: ({ row }) => <div>{row.getValue('screenSize') || '—'}</div>
  },
  {
    header: 'Address',
    accessorKey: 'address',
    cell: ({ row }) => {
      const machine = row.original
      const addressParts = [
        machine.addressLine1,
        machine.addressLine2,
        machine.city,
        machine.county,
        machine.postCode
      ].filter(Boolean)
      return <div>{addressParts.length > 0 ? addressParts.join(', ') : '—'}</div>
    }
  }
]

function Filter({ column }: { column: Column<any, unknown> }) {
  const id = useId()
  const columnFilterValue = column.getFilterValue()
  const { filterVariant } = column.columnDef.meta ?? {}
  const columnHeader = typeof column.columnDef.header === 'string' ? column.columnDef.header : ''

  const sortedUniqueValues = useMemo(() => {
    if (filterVariant === 'range') return []

    const values = Array.from(column.getFacetedUniqueValues().keys())

    const flattenedValues = values.reduce((acc: string[], curr) => {
      if (Array.isArray(curr)) {
        return [...acc, ...curr]
      }
      return [...acc, curr]
    }, [])

    return Array.from(new Set(flattenedValues)).sort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [column.getFacetedUniqueValues(), filterVariant])

  if (filterVariant === 'range') {
    return (
      <div className='*:not-first:mt-2'>
        <Label>{columnHeader}</Label>
        <div className='flex'>
          <Input
            id={`${id}-range-1`}
            className='flex-1 rounded-r-none [-moz-appearance:_textfield] focus:z-10 [&::-webkit-inner-spin-button]:m-0 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:m-0 [&::-webkit-outer-spin-button]:appearance-none'
            value={(columnFilterValue as [number, number])?.[0] ?? ''}
            onChange={e =>
              column.setFilterValue((old: [number, number]) => [
                e.target.value ? Number(e.target.value) : undefined,
                old?.[1]
              ])
            }
            placeholder='Min'
            type='number'
            aria-label={`${columnHeader} min`}
          />
          <Input
            id={`${id}-range-2`}
            className='-ms-px flex-1 rounded-l-none [-moz-appearance:_textfield] focus:z-10 [&::-webkit-inner-spin-button]:m-0 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:m-0 [&::-webkit-outer-spin-button]:appearance-none'
            value={(columnFilterValue as [number, number])?.[1] ?? ''}
            onChange={e =>
              column.setFilterValue((old: [number, number]) => [
                old?.[0],
                e.target.value ? Number(e.target.value) : undefined
              ])
            }
            placeholder='Max'
            type='number'
            aria-label={`${columnHeader} max`}
          />
        </div>
      </div>
    )
  }

  if (filterVariant === 'select') {
    return (
      <div className='*:not-first:mt-2'>
        <Label htmlFor={`${id}-select`}>{columnHeader}</Label>
        <Select
          value={columnFilterValue?.toString() ?? 'all'}
          onValueChange={value => {
            column.setFilterValue(value === 'all' ? undefined : value)
          }}
        >
          <SelectTrigger id={`${id}-select`} className='w-full'>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value='all'>All</SelectItem>
            {sortedUniqueValues.map(value => (
              <SelectItem key={String(value)} value={String(value)}>
                {String(value)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    )
  }

  return (
    <div className='*:not-first:mt-2'>
      <Label htmlFor={`${id}-input`}>{columnHeader}</Label>
      <div className='relative'>
        <Input
          id={`${id}-input`}
          className='peer pl-9'
          value={(columnFilterValue ?? '') as string}
          onChange={e => column.setFilterValue(e.target.value)}
          placeholder={`Search ${columnHeader.toLowerCase()}`}
          type='text'
        />
        <div className='text-muted-foreground/80 pointer-events-none absolute inset-y-0 left-0 flex items-center justify-center pl-3 peer-disabled:opacity-50'>
          <SearchIcon size={16} />
        </div>
      </div>
    </div>
  )
}

function exportToCSV(data: Machine[], filename: string) {
  if (data.length === 0) return

  // Get headers from columns (excluding select column)
  const headers = [
    'Machine Name',
    'Venue Type',
    'Venue Name',
    'Status',
    'Location',
    'Availability Type',
    'Available SKUs',
    'Cost',
    'Machine Type',
    'Resolution',
    'Format',
    'Screen Size',
    'Address',
    'Latitude',
    'Longitude'
  ]

  // Convert data to CSV rows
  const rows = data.map(machine => {
    const addressParts = [
      machine.addressLine1,
      machine.addressLine2,
      machine.city,
      machine.county,
      machine.postCode
    ].filter(Boolean)
    const address = addressParts.join(', ')

    return [
      machine.name || '',
      machine.venueType || '',
      machine.venueName || '',
      machine.status || '',
      machine.location || '',
      machine.availabilityType || '',
      machine.availableSkus !== null && machine.availableSkus !== undefined ? String(machine.availableSkus) : '',
      formatCurrency(machine.costCents),
      machine.machineType || '',
      machine.resolution || '',
      machine.format || '',
      machine.screenSize || '',
      address || '',
      machine.lat !== null && machine.lat !== undefined ? String(machine.lat) : '',
      machine.lng !== null && machine.lng !== undefined ? String(machine.lng) : ''
    ]
  })

  // Escape CSV values (handle commas, quotes, newlines)
  const escapeCSV = (value: string) => {
    if (value.includes(',') || value.includes('"') || value.includes('\n')) {
      return `"${value.replace(/"/g, '""')}"`
    }
    return value
  }

  // Combine headers and rows
  const csvContent = [
    headers.map(escapeCSV).join(','),
    ...rows.map(row => row.map(escapeCSV).join(','))
  ].join('\n')

  // Create blob and download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  const url = URL.createObjectURL(blob)
  link.setAttribute('href', url)
  link.setAttribute('download', filename)
  link.style.visibility = 'hidden'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

export default function Export() {
  const { toast } = useToast()
  const [machines, setMachines] = useState<Machine[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [sorting, setSorting] = useState<SortingState>([])

  useEffect(() => {
    async function loadMachines() {
      try {
        setLoading(true)
        setError(null)
        const data = await getMachines()
        setMachines(data)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load machines')
        toast({
          title: 'Error',
          description: 'Failed to load machines. Please try again.',
          variant: 'destructive'
        })
      } finally {
        setLoading(false)
      }
    }
    loadMachines()
  }, [toast])

  const table = useReactTable({
    data: machines,
    columns,
    state: {
      sorting,
      columnFilters
    },
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
    onSortingChange: setSorting,
    enableSortingRemoval: false
  })

  const handleExport = () => {
    const selectedRows = table.getFilteredSelectedRowModel().rows
    const dataToExport = selectedRows.length > 0 
      ? selectedRows.map(row => row.original)
      : table.getFilteredRowModel().rows.map(row => row.original)
    
    if (dataToExport.length === 0) {
      toast({
        title: 'No Data',
        description: 'No data available to export.',
        variant: 'destructive'
      })
      return
    }

    const timestamp = new Date().toISOString().split('T')[0]
    const filename = `machines-export-${timestamp}.csv`
    exportToCSV(dataToExport, filename)
    
    toast({
      title: 'Export Successful',
      description: `Exported ${dataToExport.length} machine${dataToExport.length > 1 ? 's' : ''} to CSV.`
    })
  }

  if (loading) {
    return (
      <div className="w-full px-2 md:px-4 py-4 md:py-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="text-lg font-semibold text-slate-900">Loading machines...</div>
            <div className="text-sm text-slate-500 mt-2">Please wait</div>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="w-full px-2 md:px-4 py-4 md:py-6">
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
          {error}
        </div>
      </div>
    )
  }

  return (
    <div className="w-full px-2 md:px-4 py-4 md:py-6">
      <div className="flex flex-col gap-6">
        {/* Header */}
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Export Machines</h1>
            <p className="text-sm text-slate-500 mt-1">
              Filter, sort, and export machine data to CSV
            </p>
          </div>
          <Button
            onClick={handleExport}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            <Download className="h-4 w-4 mr-2" />
            Export Machine List
          </Button>
        </div>

        {/* Filters */}
        <div className='rounded-md border border-slate-200 bg-white'>
          <div className='flex flex-wrap gap-3 px-4 py-4'>
            <div className='w-full sm:w-48'>
              <Filter column={table.getColumn('name')!} />
            </div>
            <div className='w-full sm:w-40'>
              <Filter column={table.getColumn('venueType')!} />
            </div>
            <div className='w-full sm:w-40'>
              <Filter column={table.getColumn('status')!} />
            </div>
            <div className='w-full sm:w-40'>
              <Filter column={table.getColumn('location')!} />
            </div>
            <div className='w-full sm:w-40'>
              <Filter column={table.getColumn('availableSkus')!} />
            </div>
            <div className='w-full sm:w-40'>
              <Filter column={table.getColumn('costCents')!} />
            </div>
          </div>

          {/* Table */}
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map(headerGroup => (
                <TableRow key={headerGroup.id} className='bg-muted/50'>
                  {headerGroup.headers.map(header => {
                    return (
                      <TableHead key={header.id} className='relative h-10 border-t select-none'>
                        {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                      </TableHead>
                    )
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map(row => (
                  <TableRow key={row.id} data-state={row.getIsSelected() && 'selected'}>
                    {row.getVisibleCells().map(cell => (
                      <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={columns.length} className='h-24 text-center'>
                    No results.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        {/* Footer Info */}
        <div className="text-sm text-slate-500 text-center">
          Showing {table.getFilteredRowModel().rows.length} of {machines.length} machines
          {table.getFilteredSelectedRowModel().rows.length > 0 && (
            <span className="ml-2">
              ({table.getFilteredSelectedRowModel().rows.length} selected)
            </span>
          )}
        </div>
      </div>
    </div>
  )
}

